import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { SESSION_EXPIRED } from '../../actions/types';
import { getAccessToken } from '../auth';
import config from '../../assets/config';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiUrl.restServer,
  prepareHeaders: (headers) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      // headers.set('x-prerender-authorization', accessToken);
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

const baseQueryWith401 = async (args, api, extraOptions) => {
  // Once the session is expired, fail fast with the same shape a real 401 would have —
  // no point hitting the server without credentials. (401 is never retried below.)
  if (api.getState().keycloak.sessionExpired) {
    return { error: { status: 401, data: 'Session expired' } };
  }
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) {
    api.dispatch({ type: SESSION_EXPIRED });
  }
  return result;
};

export const MAX_RETRIES = 3;

/**
 * A retried request must be safe to repeat. A read always is. A write is only
 * repeated when its endpoint opts in with `extraOptions: { retryOnServerError: true }`,
 * which its owner sets after checking the write is idempotent — dropping a write on a
 * transient 5xx is silent, and for the onboarding survey it used to leave the user in
 * a login redirect loop (PRE-3350).
 */
export function shouldRetry(error, args, { attempt, extraOptions }) {
  if (attempt > MAX_RETRIES) {
    return false;
  }

  // Pre-existing behaviour, kept as it is: every method is retried on FETCH_ERROR.
  // Note that this is NOT proof the request never reached the server — RTK Query
  // reports FETCH_ERROR for any rejected fetch, a connection dropped after the
  // request was sent included. Writes that never opted in are therefore still
  // retried here and can be applied twice. Narrowing it needs its own change.
  if (error?.status === 'FETCH_ERROR') {
    return true;
  }

  const isServerError = error?.status >= 500 && error?.status <= 599;
  const isRead = typeof args === 'string' || args?.method === 'GET'; // query: () => '/path' || ({ url: '/path', method: 'GET' })

  return isServerError && (isRead || Boolean(extraOptions?.retryOnServerError));
}

export const restApi = createApi({
  baseQuery: retry(baseQueryWith401, { retryCondition: shouldRetry }),
  tagTypes: [
    'EnvironmentHistory',
    'Environment',
    'Coupon',
    'TeamInvites',
    'AuditLog',
    'domainStatistics',
    'domainDetails',
    'rankedDomains',
    'availableDomains',
    'schedulerSettings',
    'URL',
    'CacheClearStatus',
    'Notifications',
    'CachedPages',
    'URLParameters',
    'Domains',
    'AccountDetails',
    'IntegrationDomain',
    'RecacheQueue',
    'SurveyAnswers',
    'Plan',
    'Invoices',
    'GscIntegration',
    'GscConnectionStatus',
    'DiagnosticsReports',
    'DiscoverabilityStatus',
    'DiscoverabilityKpis',
    'DiscoverabilityCharts',
    'DiscoverabilityPages',
    'CommsConsent',
    'ConnectedCfTokens',
  ],
  endpoints: () => ({}),
});
